/**
 * Phase 14.5 Step 3 — `/degradation-profiles` real list page.
 *
 * Replaces the Step 1 placeholder. Renders a paged + name-searchable
 * list of degradation profiles. Per-row affordances:
 *   • Open      — link to /degradation-profiles/:id
 *   • Duplicate — populates a Create form pre-seeded with this row
 *                 (TODO Step 4: pass via location.state. For now, the
 *                 operator can click "Apply Friendly/Stress Reference"
 *                 in the editor — covered by the seeded-preset path.)
 *   • Delete    — confirm dialog → DELETE → re-fetch (disabled on seeded)
 *
 * Empty-state copy preserves the Step 1 testid
 * `degradation-profiles-list-placeholder` per Y2 — that is now the
 * legitimate empty-state affordance, not a stub marker.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layers, Plus, Search, Trash2, ExternalLink, Loader2, Copy } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import degradationProfiles from "@/api/degradationProfiles";

const PAGE_SIZE = 20;

export default function DegradationProfilesListPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    // Debounce the search box — 300 ms.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    const fetchPage = () => {
        setLoading(true);
        setErr(null);
        degradationProfiles
            .list({
                page,
                page_size: PAGE_SIZE,
                ...(debouncedSearch ? { name_search: debouncedSearch } : {}),
            })
            .then((body) => {
                setItems(Array.isArray(body?.items) ? body.items : []);
                setTotal(typeof body?.total === "number" ? body.total : (body?.items?.length ?? 0));
            })
            .catch((e) => setErr(e?.message || "Failed to load profiles."))
            .finally(() => setLoading(false));
    };

    useEffect(fetchPage, [page, debouncedSearch]);

    const handleDelete = async (id) => {
        setDeletingId(id);
        try {
            await degradationProfiles.remove(id);
            fetchPage();
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Delete failed.";
            setErr(detail);
        } finally {
            setDeletingId(null);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-4" data-testid="degradation-profiles-list-page">
            <PageHeader
                title="Degradation profiles"
                subtitle="Library of post-engine degradation profiles — IMU + radar bias / drift, Markov-chain track quality, data-starvation + latency, and modulation thresholds. Attached to a Geometry campaign at submission time."
                actions={[
                    <Button
                        key="new"
                        asChild
                        data-testid="degradation-profiles-new-button"
                        className="gap-2"
                    >
                        <Link to="/degradation-profiles/new">
                            <Plus className="size-4" />
                            New profile
                        </Link>
                    </Button>,
                ]}
            />

            <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-sm">
                    <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-ux-text-3" />
                    <Input
                        placeholder="Search by name…"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        className="pl-8"
                        data-testid="degradation-profiles-search"
                    />
                </div>
                {loading && (
                    <Loader2 className="size-4 animate-spin text-ux-text-3" data-testid="degradation-profiles-loading-indicator" />
                )}
            </div>

            {err && (
                <Alert variant="destructive" data-testid="degradation-profiles-list-error">
                    <AlertDescription>{String(err)}</AlertDescription>
                </Alert>
            )}

            {!loading && items.length === 0 && !err && (
                <div
                    className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-8 text-center text-sm text-ux-text-3"
                    data-testid="degradation-profiles-list-placeholder"
                >
                    <Layers className="mx-auto mb-3 size-6 opacity-60" />
                    <p className="font-medium text-ux-text-2">
                        {debouncedSearch ? "No profiles match that search." : "No profiles yet."}
                    </p>
                    <p className="mt-1">
                        {debouncedSearch
                            ? "Try a different name fragment."
                            : "Create your first profile or apply the seeded Friendly Default / Stress Reference presets."}
                    </p>
                </div>
            )}

            {items.length > 0 && (
                <ul
                    className="divide-y divide-ux-border-1 rounded-md border border-ux-border-1 bg-ux-surface-1"
                    data-testid="degradation-profiles-list"
                >
                    {items.map((p) => (
                        <li
                            key={p.id}
                            className="grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-3"
                            data-testid={`degradation-profiles-row-${p.id}`}
                        >
                            <div>
                                <div className="flex items-baseline gap-2">
                                    <Link
                                        to={`/degradation-profiles/${p.id}`}
                                        className="text-sm font-medium text-ux-text-1 underline-offset-2 hover:underline"
                                        data-testid={`degradation-profiles-row-name-${p.id}`}
                                    >
                                        {p.name}
                                    </Link>
                                    {p.is_seeded && (
                                        <span
                                            className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-300"
                                            data-testid={`degradation-profiles-row-seeded-${p.id}`}
                                        >
                                            seeded
                                        </span>
                                    )}
                                </div>
                                {p.description && (
                                    <p className="text-xs text-ux-text-3">{p.description}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-1">
                                <Button
                                    asChild
                                    variant="ghost"
                                    size="sm"
                                    data-testid={`degradation-profiles-row-open-${p.id}`}
                                >
                                    <Link to={`/degradation-profiles/${p.id}`}>
                                        <ExternalLink className="size-3" />
                                        Open
                                    </Link>
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate("/degradation-profiles/new", {
                                        state: { duplicate_from: p },
                                    })}
                                    data-testid={`degradation-profiles-row-duplicate-${p.id}`}
                                    aria-label={`Duplicate ${p.name}`}
                                >
                                    <Copy className="size-3" />
                                    Duplicate
                                </Button>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled={p.is_seeded || deletingId === p.id}
                                            data-testid={`degradation-profiles-row-delete-${p.id}`}
                                            aria-label={`Delete ${p.name}`}
                                        >
                                            <Trash2 className="size-3" />
                                            Delete
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent
                                        data-testid={`degradation-profiles-row-delete-dialog-${p.id}`}
                                    >
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete this profile?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This permanently removes <strong>{p.name}</strong>. Geometry
                                                campaigns that previously referenced it keep their persisted
                                                snapshot but will not see further updates.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel
                                                data-testid={`degradation-profiles-row-delete-cancel-${p.id}`}
                                            >
                                                Cancel
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => handleDelete(p.id)}
                                                data-testid={`degradation-profiles-row-delete-confirm-${p.id}`}
                                            >
                                                Delete
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {totalPages > 1 && (
                <div className="flex items-center justify-between text-xs text-ux-text-3">
                    <span data-testid="degradation-profiles-page-readout">
                        Page {page} of {totalPages} · {total} profiles
                    </span>
                    <div className="flex gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage(page - 1)}
                            data-testid="degradation-profiles-page-prev"
                        >
                            ← Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                            data-testid="degradation-profiles-page-next"
                        >
                            Next →
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
